import { Button, Card, Form, Input } from '@dittolive/anvil'
import { yupResolver } from '@hookform/resolvers/yup'
import { ComponentProps, forwardRef, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

const schema = yup.object({
  name: yup.string().when('$signup', {
    is: true,
    then: (field) => field.trim().required('Name is required'),
  }),
  email: yup
    .string()
    .trim()
    .email('Enter a valid email')
    .required('Email is required'),
  password: yup
    .string()
    .required('Password is required')
    .when('$signup', {
      is: true,
      then: (field) =>
        field
          .min(8, 'Use at least 8 characters')
          .max(128, 'Use at most 128 characters'),
    }),
  confirmPassword: yup.string().when('$signup', {
    is: true,
    then: (field) =>
      field
        .required('Confirm your password')
        .oneOf([yup.ref('password')], 'Passwords must match'),
  }),
})

export type Credentials = yup.InferType<typeof schema>

const CredentialInput = forwardRef<
  HTMLInputElement,
  ComponentProps<typeof Input>
>(function CredentialInput({ id, errorMessage, ...props }, ref) {
  return (
    <div className="space-y-2">
      <Input
        {...props}
        ref={ref}
        id={id}
        isInvalid={!!errorMessage}
        aria-invalid={!!errorMessage}
        aria-describedby={errorMessage ? `${id}-error` : undefined}
      />
      {errorMessage && (
        <p
          id={`${id}-error`}
          role="alert"
          className="text-fill-critical text-sm"
        >
          {errorMessage}
        </p>
      )}
    </div>
  )
})

export function CredentialsForm({
  signup,
  busy,
  onSubmit,
}: {
  signup: boolean
  busy: boolean
  onSubmit: (values: Credentials) => Promise<void>
}) {
  const [showPassword, setShowPassword] = useState(false)
  const form = useForm<Credentials>({
    resolver: yupResolver(schema),
    context: { signup },
    defaultValues: { name: '', email: '', password: '', confirmPassword: '' },
  })
  const { errors, isSubmitting, submitCount } = form.formState
  useEffect(() => {
    // Anvil disables the fieldset during validation. Focus after it is enabled.
    if (submitCount > 0 && !isSubmitting) {
      const firstError = (
        ['name', 'email', 'password', 'confirmPassword'] as const
      ).find((field) => errors[field])
      if (firstError) form.setFocus(firstError)
    }
  }, [errors, form, isSubmitting, submitCount])
  return (
    <Card>
      <Card.Body>
        <Form
          form={form}
          onSubmit={onSubmit}
          disabled={busy}
          noValidate
          fieldsetClassName="gap-5"
          aria-busy={busy}
        >
          {signup && (
            <CredentialInput
              id="signup-name"
              label="Name"
              autoComplete="name"
              required
              errorMessage={form.formState.errors.name?.message}
              {...form.register('name')}
            />
          )}
          <CredentialInput
            id="login-email"
            label="Email"
            type="email"
            autoComplete="email"
            required
            errorMessage={form.formState.errors.email?.message}
            {...form.register('email')}
          />
          <CredentialInput
            id="login-password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            autoComplete={signup ? 'new-password' : 'current-password'}
            required
            errorMessage={form.formState.errors.password?.message}
            data-sentry-mask
            {...form.register('password')}
          />
          <Button
            type="button"
            variant="secondary"
            onClick={() => setShowPassword((shown) => !shown)}
            aria-pressed={showPassword}
            disabled={busy}
          >
            {showPassword ? 'Hide password' : 'Show password'}
          </Button>
          {signup && (
            <CredentialInput
              id="confirm-password"
              label="Confirm password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              errorMessage={form.formState.errors.confirmPassword?.message}
              data-sentry-mask
              {...form.register('confirmPassword')}
            />
          )}
          <Button type="submit" variant="primary" disabled={busy}>
            {busy
              ? signup
                ? 'Creating account…'
                : 'Logging in…'
              : signup
                ? 'Create account'
                : 'Log in'}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  )
}
